// The ⋯ button in the channel header on phones (#348): a small menu with Members, Pins and Notifications, so the
// header holds only the name, the live dot, ⋯ and Search. The rows come from `headerMenuRows` (shared/src/headerMenu.ts,
// cases in shared/cases/header-menu.json). Wide layouts keep their own buttons for these.
import { useEffect, useRef, useState } from "react";
import { headerMenuRows, type HeaderMenuId, type HeaderMenuInput } from "@bloom/shared";
import "./header-more.css";

const ICONS: Record<HeaderMenuId, React.ReactNode> = {
  members: (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M16 20v-1.5a3.5 3.5 0 0 0-3.5-3.5h-5A3.5 3.5 0 0 0 4 18.5V20" /><circle cx="10" cy="8" r="3.5" /><path d="M20 20v-1.5a3.5 3.5 0 0 0-2.5-3.35M15.5 4.7a3.5 3.5 0 0 1 0 6.6" /></svg>
  ),
  pins: (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M9 4h6l-1 6 3 3H7l3-3z" /><path d="M12 13v7" /></svg>
  ),
  notifications: (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M6 16V11a6 6 0 0 1 12 0v5l2 2H4z" /><path d="M10 20a2 2 0 0 0 4 0" /></svg>
  ),
};

export function HeaderMore({ input, on }: { input: HeaderMenuInput; on: Record<HeaderMenuId, () => void> }) {
  const [open, setOpen] = useState(false);
  const wrap = useRef<HTMLSpanElement>(null);
  const button = useRef<HTMLButtonElement>(null);
  const rows = headerMenuRows(input);

  // Closes on a tap outside or Escape (Escape hands focus back to ⋯). Opens with the first row focused.
  useEffect(() => {
    if (!open) return;
    wrap.current?.querySelector<HTMLElement>('[role="menuitem"]')?.focus();
    const onDown = (e: PointerEvent) => !wrap.current?.contains(e.target as Node) && setOpen(false);
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.preventDefault();
        setOpen(false);
        button.current?.focus();
      }
    };
    document.addEventListener("pointerdown", onDown);
    document.addEventListener("keydown", onKey, true);
    return () => {
      document.removeEventListener("pointerdown", onDown);
      document.removeEventListener("keydown", onKey, true);
    };
  }, [open]);

  const arrows = (e: React.KeyboardEvent) => {
    if (e.key !== "ArrowDown" && e.key !== "ArrowUp") return;
    e.preventDefault();
    const items = [...(wrap.current?.querySelectorAll<HTMLElement>('[role="menuitem"]') ?? [])];
    const at = items.indexOf(document.activeElement as HTMLElement);
    items[(at + (e.key === "ArrowDown" ? 1 : items.length - 1)) % items.length]?.focus();
  };

  return (
    <span className="more-wrap mobile-only" ref={wrap}>
      <button
        ref={button}
        type="button"
        className={`icon-btn head-btn more-btn${open ? " on" : ""}`}
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={`More: ${rows.map((r) => r.label).join(", ")}`}
        title="More"
      >
        <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><circle cx="5" cy="12" r="2" /><circle cx="12" cy="12" r="2" /><circle cx="19" cy="12" r="2" /></svg>
      </button>
      {open && (
        <div className="more-menu" role="menu" aria-label="More" onKeyDown={arrows}>
          {rows.map((r) => (
            <button
              key={r.id}
              type="button"
              role="menuitem"
              className="more-item"
              onClick={() => {
                setOpen(false);
                on[r.id]();
              }}
            >
              <span className="more-label">{r.label}</span>
              {r.hint && <span className="more-hint">{r.hint}</span>}
              {ICONS[r.id]}
            </button>
          ))}
        </div>
      )}
    </span>
  );
}
